import { logUsage } from 'owa-analytics';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';

/**
 * Telemetry for the tentative "request-to-book" flow gated by the book-policy
 * flights (`msplaces-space-finder-book-policy` for the Space Finder surface,
 * `msplaces-place-finder-book-policy` for the Places Finder surface). These
 * events back the rollout story that tentative spaces — previously rendered as
 * Unavailable and impossible to book — now convert into real reservations via
 * the Request button.
 *
 * Shared by both surfaces; the `Surface` property distinguishes them so a single
 * funnel/dashboard can segment by surface. All events use `logUsage`, so custom
 * properties land in the `MiscData` column as JSON and are parsed with
 * `parse_json(MiscData)` in Kusto. See
 * docs/SpaceFinderTentativeRequest_SUCCESS_METRICS.md for the metric definitions.
 *
 * Clicked → Submitted is correlated in Kusto by `Session_Id` + `PlaceId` rather
 * than a threaded id, to avoid plumbing a correlation id through the many
 * intermediate card props. (PlacesFinder has no dialog terminal state, so it
 * emits Exposure + Clicked only; the booking outcome resolves at calendar save.)
 */

export type SpaceFinderSurface = 'SpaceFinder' | 'PlacesFinder';

export interface TentativeExposureData {
    /** 'Room' or 'Desk' — the search mode that produced the result list. */
    spaceType: string;
    /** Total spaces returned for the search. */
    totalCount: number;
    /** Count with policyEvaluation.Status === 'Tentative' (the request options shown). */
    tentativeCount: number;
    /** Tentative spaces that are available — free, but still require approval. */
    tentativeFreeCount: number;
    /** Tentative spaces that are busy — requestable, but were pure Unavailable in the legacy UI. */
    tentativeBusyCount: number;
    /** True when the list is the zero-query suggestion list rather than searched results. */
    fromSuggestion?: boolean;
}

/**
 * One event per completed search. Provides the funnel denominator and the
 * tentative breakdown (free-but-approval-required vs busy-but-requestable). A
 * non-zero TentativeBusyCount is the rescue signal: those busy spaces were
 * unbookable (rendered Unavailable) before the request flow.
 */
export function logTentativeExposure(
    surface: SpaceFinderSurface,
    data: TentativeExposureData
): void {
    logUsage('PlacesSpaceFinderTentativeExposure', getModuleContextMailboxInfo(), {
        Surface: surface,
        SpaceType: data.spaceType,
        TotalCount: data.totalCount,
        TentativeCount: data.tentativeCount,
        TentativeFreeCount: data.tentativeFreeCount,
        TentativeBusyCount: data.tentativeBusyCount,
        FromSuggestion: data.fromSuggestion ?? false,
    });
}

export interface SpaceRequestClickedData {
    placeId: string;
    /** 'Room' or 'Desk'. */
    spaceType: string;
    /** true = free but needs approval; false = busy but still requestable. */
    isFree: boolean;
    /** Reserve is the default request funnel action; Add is used by hosted room selection. */
    action?: 'Add' | 'Reserve';
}

/** Fired when the user presses the Request button on a tentative space. */
export function logSpaceRequestClicked(
    surface: SpaceFinderSurface,
    data: SpaceRequestClickedData
): void {
    logUsage('PlacesSpaceFinderRequest', getModuleContextMailboxInfo(), {
        Surface: surface,
        Stage: 'Clicked',
        Action: data.action ?? 'Reserve',
        PlaceId: data.placeId,
        SpaceType: data.spaceType,
        IsFree: data.isFree,
    });
}

export interface SpaceRequestSubmittedData {
    placeId: string;
    /** 'Room' or 'Desk'. */
    spaceType: string;
    result: 'Success' | 'Failure';
    /** Scrubbed failure reason; omit on success. */
    errorReason?: string;
}

/**
 * Fired when the tentative request reaches a terminal state in the outro dialog.
 * Only the Space Finder surface has such a dialog; Places Finder resolves the
 * booking at calendar save and does not emit this stage.
 */
export function logSpaceRequestSubmitted(
    surface: SpaceFinderSurface,
    data: SpaceRequestSubmittedData
): void {
    logUsage('PlacesSpaceFinderRequest', getModuleContextMailboxInfo(), {
        Surface: surface,
        Stage: 'Submitted',
        PlaceId: data.placeId,
        SpaceType: data.spaceType,
        Result: data.result,
        ErrorReason: data.errorReason,
    });
}
